import React from 'react';
import './References.css';

// Import ikon certyfikatów i referencji
import { FileText, Award, Users, CheckCircle, Shield, Zap, Eye } from 'lucide-react';

const References = () => {
  // Dane certyfikatów - poprawione opisy
  const certificates = [
    {
      id: 1,
      title: 'Certyfikat Projektowania Sieci Światłowodowych',
      description: 'Certyfikat kursu projektowania budowy, instalacji oraz pomiarów w sieciach światłowodowych',
      fileName: 'CCF20090823_00001.pdf',
      icon: <Eye className="w-8 h-8" />,
      category: 'Światłowody'
    },
    {
      id: 2,
      title: 'Świadectwa Kwalifikacyjne',
      description: 'Oficjalne świadectwa potwierdzające kwalifikacje zawodowe w branży elektrycznej',
      fileName: 'Rarloor17052023.pdf',
      icon: <Award className="w-8 h-8" />,
      category: 'Kwalifikacje'
    },
    {
      id: 3,
      title: 'Certyfikat Instalatora Schrack',
      description: 'Certyfikat upoważniający do instalacji systemów i urządzeń firmy Schrack',
      fileName: 'Rarloor26082025.pdf',
      icon: <Zap className="w-8 h-8" />,
      category: 'Instalacje'
    },
    {
      id: 4,
      title: 'Certyfikat Ochrony Przeciwpożarowej',
      description: 'Certyfikat ukończenia szkolenia z zakresu ochrony przeciwpożarowej',
      fileName: 'Rarloor26082025_0001.pdf',
      icon: <Shield className="w-8 h-8" />,
      category: 'Bezpieczeństwo'
    },
    {
      id: 5,
      title: 'Certyfikat Voltea Poland',
      description: 'Certyfikat potwierdzający uprawnienia do pracy z systemami Voltea Poland',
      fileName: 'Rarloor26082025_0002.pdf',
      icon: <CheckCircle className="w-8 h-8" />,
      category: 'Systemy'
    }
  ];

  // Dane referencji - poprawione opisy
  const references = [
    {
      id: 4,
      title: 'Referencja od Komendy Wojewódzkiej PSP w Poznaniu',
      description: 'Potwierdzenie należytego i terminowego wykonania modernizacji instalacji teletechnicznych i elektrycznych w siedzibie Komendy',
      fileName: 'referencje-kw-psp-poznan.pdf',
      clientType: 'Komenda Wojewódzka PSP w Poznaniu',
      date: '2026'
    },
    {
      id: 1,
      title: 'Referencja od firmy ELMAR',
      description: 'Pozytywna ocena wykonanych prac elektrycznych i profesjonalnej obsługi klienta',
      fileName: 'Rarloor07072025.pdf',
      clientType: 'Firma ELMAR',
      date: '2025'
    },
    {
      id: 2,
      title: 'Referencja od firmy Projekt Budowa',
      description: 'Referencja potwierdzająca wysoką jakość realizacji projektu budowlanego',
      fileName: 'Referencje Projekt Budowa.pdf',
      clientType: 'Projekt Budowa',
      date: '2024'
    },
    {
      id: 3,
      title: 'Referencja od firmy Reesco',
      description: 'Potwierdzenie profesjonalnego wykonania zleconych prac instalacyjnych',
      fileName: 'SDrukarka P25070714080.pdf',
      clientType: 'Firma Reesco',
      date: '2025'
    }
  ];

  const openPDF = (fileName, type) => {
    // Poprawiona ścieżka - usuń 'assets/' jeśli pliki są w folderze public
    const path = type === 'cert' 
      ? `/files/certs/${fileName}` 
      : `/files/references/${fileName}`;
    
    // Sprawdź czy plik istnieje przed otwarciem
    const link = document.createElement('a');
    link.href = path;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    
    // Dodaj obsługę błędów
    link.onerror = () => {
      alert('Plik nie został znaleziony. Skontaktuj się z administratorem.');
    };
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="references-page">
      {/* Hero Section */}
      <section className="references-hero">
        <div className="hero-content">
          <div className="hero-header animate-fade-in-up">
            <h1>Certyfikaty i Referencje</h1>
            <p className="hero-subtitle">
              Poznaj nasze kwalifikacje i opinie zadowolonych klientów. 
              Wszystkie dokumenty dostępne są w formacie PDF.
            </p>
          </div>
        </div>
      </section>

      {/* Certificates Section */}
      <section className="certificates-section section">
        <div className="container">
          <div className="section-header animate-fade-in-up">
            <h2 className="section-title">Nasze Certyfikaty</h2>
            <p className="section-description">
              Posiadamy wszystkie wymagane certyfikaty i uprawnienia do wykonywania 
              prac elektrycznych, teletechnicznych oraz światłowodowych.
            </p>
          </div>
          
          <div className="certificates-grid">
            {certificates.map((cert, index) => (
              <div 
                key={cert.id} 
                className={`certificate-card animate-fade-in-up animation-delay-${(index + 1) * 100}`}
                onClick={() => openPDF(cert.fileName, 'cert')}
              >
                <div className="certificate-icon">
                  {cert.icon}
                </div>
                <div className="certificate-content">
                  <span className="certificate-category">{cert.category}</span>
                  <h3>{cert.title}</h3>
                  <p>{cert.description}</p>
                  <div className="certificate-action">
                    <FileText className="w-4 h-4" />
                    <span>Otwórz PDF</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* References Section */}
      <section className="references-section section">
        <div className="container">
          <div className="section-header animate-fade-in-up">
            <h2 className="section-title">Referencje Klientów</h2>
            <p className="section-description">
              Przeczytaj opinie naszych zadowolonych klientów - Komendy Wojewódzkiej PSP w Poznaniu
              oraz firm ELMAR, Projekt Budowa i Reesco - o jakości wykonanych prac i profesjonalnej obsłudze.
            </p>
          </div>
          
          <div className="references-grid">
            {references.map((ref, index) => (
              <div 
                key={ref.id} 
                className={`reference-card animate-fade-in-up animation-delay-${(index + 1) * 150}`}
                onClick={() => openPDF(ref.fileName, 'ref')}
              >
                <div className="reference-header">
                  <div className="reference-icon">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="reference-meta">
                    <span className="reference-type">{ref.clientType}</span>
                    <span className="reference-date">{ref.date}</span>
                  </div>
                </div>
                <div className="reference-content">
                  <h3>{ref.title}</h3>
                  <p>{ref.description}</p>
                  <div className="reference-action">
                    <FileText className="w-4 h-4" />
                    <span>Przeczytaj referencje</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content animate-fade-in-up">
            <h2>Przekonaj się sam</h2>
            <p>Dołącz do grona naszych zadowolonych klientów i skorzystaj z naszych usług</p>
            <a href="/kontakt" className="btn btn-primary btn-large">
              Skontaktuj się z nami
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default References;